import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, Minus, Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../../context/CartContext";
import { api, formatApiError } from "../../lib/api";
import { IMAGES } from "../../lib/data";

const FULFILLMENT_LABELS = { pickup: "Pickup", local_delivery: "Local Delivery", shipping: "Shipping" };
const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export const CartDrawer = () => {
    const { items, subtotal, removeFromCart, updateQty, cartOpen, setCartOpen } = useCart();
    const [cfg, setCfg] = useState(null);
    const [method, setMethod] = useState("");
    const [zip, setZip] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/settings/public")
            .then((res) => {
                const f = res.data?.fulfillment || {};
                setCfg(f);
                const enabled = ["local_delivery", "shipping", "pickup"].filter((k) => f[k]);
                setMethod(enabled[0] || "");
            })
            .catch(() => setCfg({}));
    }, []);

    const enabled = cfg ? ["local_delivery", "shipping", "pickup"].filter((k) => cfg[k]) : [];
    const subtotalCents = Math.round(subtotal * 100);

    let feeCents = 0;
    let feeNote = "";
    if (method === "local_delivery" && cfg) {
        feeCents = cfg.local_delivery_fee_cents || 0;
        if (cfg.local_delivery_free_over_cents && subtotalCents >= cfg.local_delivery_free_over_cents) {
            feeCents = 0;
            feeNote = "Free delivery unlocked";
        } else if (cfg.local_delivery_free_over_cents) {
            feeNote = `Free delivery over ${money(cfg.local_delivery_free_over_cents)}`;
        }
    }
    if (method === "shipping" && cfg) {
        feeCents = cfg.shipping_fee_cents || 0;
        if (cfg.shipping_free_over_cents && subtotalCents >= cfg.shipping_free_over_cents) {
            feeCents = 0;
            feeNote = "Free shipping unlocked";
        } else if (cfg.shipping_free_over_cents) {
            feeNote = `Free shipping over ${money(cfg.shipping_free_over_cents)}`;
        }
    }

    const checkout = async () => {
        setLoading(true);
        try {
            const res = await api.post("/checkout", {
                items: items.map((i) => ({ id: i.id, qty: i.qty })),
                fulfillment: method,
                zip: zip || null,
                email: email || null,
                origin_url: window.location.origin,
            });
            window.location.href = res.data.checkout_url;
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Checkout is temporarily unavailable.");
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {cartOpen && (
                <>
                    <motion.div
                        data-testid="cart-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setCartOpen(false)}
                        className="fixed inset-0 z-[70] bg-black/60"
                    />
                    <motion.aside
                        data-testid="cart-drawer"
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 30, stiffness: 300 }}
                        className="fixed top-0 right-0 bottom-0 z-[71] w-full max-w-md bg-white flex flex-col"
                    >
                        <div className="flex items-center justify-between px-6 h-16 border-b border-zinc-200">
                            <h2 className="font-oswald uppercase text-xl tracking-wide">Your Cart</h2>
                            <button
                                onClick={() => setCartOpen(false)}
                                data-testid="cart-close-button"
                                aria-label="Close cart"
                                className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-paper"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-4">
                            {items.length === 0 ? (
                                <p data-testid="cart-empty-message" className="text-zinc-500 text-sm pt-10 text-center">
                                    The cart is empty. The show must go on — go pick your fun.
                                </p>
                            ) : (
                                <ul className="divide-y divide-zinc-100">
                                    {items.map((item) => (
                                        <li key={item.id} data-testid={`cart-item-${item.id}`} className="py-4 flex gap-4 items-center">
                                            <img
                                                src={item.image || (item.kind === "popcorn" ? IMAGES.popcorn : IMAGES.cottonCandy)}
                                                alt={item.name}
                                                className="h-16 w-16 rounded-2xl object-cover bg-paper"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-oswald uppercase text-sm leading-tight">{item.name}</p>
                                                <p className="text-xs text-zinc-500 mt-0.5">${Number(item.price).toFixed(2)} each</p>
                                                <div className="mt-2 inline-flex items-center gap-3 border border-zinc-200 rounded-full px-2 py-1">
                                                    <button onClick={() => updateQty(item.id, -1)} data-testid={`cart-qty-minus-${item.id}`} aria-label="Decrease quantity" className="h-6 w-6 flex items-center justify-center rounded-full hover:bg-paper">
                                                        <Minus size={12} />
                                                    </button>
                                                    <span className="text-sm font-bold w-4 text-center">{item.qty}</span>
                                                    <button onClick={() => updateQty(item.id, 1)} data-testid={`cart-qty-plus-${item.id}`} aria-label="Increase quantity" className="h-6 w-6 flex items-center justify-center rounded-full hover:bg-paper">
                                                        <Plus size={12} />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold text-sm">${(item.qty * Number(item.price)).toFixed(2)}</p>
                                                <button onClick={() => removeFromCart(item.id)} data-testid={`cart-remove-${item.id}`} aria-label={`Remove ${item.name}`} className="mt-2 h-8 w-8 inline-flex items-center justify-center rounded-full text-zinc-400 hover:text-pink-dark hover:bg-paper transition-colors">
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        {items.length > 0 && (
                            <div className="border-t border-zinc-200 px-6 py-5 space-y-4">
                                {enabled.length === 0 ? (
                                    <p className="text-sm text-zinc-500">Checkout is being finalized — no fulfillment methods are enabled yet.</p>
                                ) : (
                                    <>
                                        <div>
                                            <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-zinc-500 mb-2">Delivery Method</p>
                                            <div className="flex gap-2" data-testid="cart-fulfillment-options">
                                                {enabled.map((f) => (
                                                    <button
                                                        key={f}
                                                        onClick={() => setMethod(f)}
                                                        data-testid={`cart-fulfillment-${f}`}
                                                        className={`flex-1 rounded-full px-3 py-2.5 font-oswald uppercase tracking-widest text-[11px] transition-colors duration-300 ${
                                                            method === f ? "bg-ink text-white" : "border border-zinc-300 text-zinc-600 hover:border-pink"
                                                        }`}
                                                    >
                                                        {FULFILLMENT_LABELS[f]}
                                                    </button>
                                                ))}
                                            </div>
                                            {method === "local_delivery" && (
                                                <div className="mt-3 space-y-2">
                                                    <input
                                                        value={zip}
                                                        onChange={(e) => setZip(e.target.value)}
                                                        placeholder="Delivery ZIP code"
                                                        data-testid="cart-zip-input"
                                                        className="w-full rounded-full bg-white border border-zinc-300 px-5 py-3 text-sm outline-none focus:ring-2 focus:ring-pink placeholder:text-zinc-400"
                                                    />
                                                    {cfg?.local_delivery_note && <p className="text-xs text-zinc-500">{cfg.local_delivery_note}</p>}
                                                    {cfg?.local_delivery_timing && <p className="text-xs text-zinc-500">{cfg.local_delivery_timing}</p>}
                                                </div>
                                            )}
                                            {method === "shipping" && (
                                                <div className="mt-3 space-y-1">
                                                    {cfg?.shipping_processing && <p className="text-xs text-zinc-500">{cfg.shipping_processing}</p>}
                                                    {cfg?.shipping_note && <p className="text-xs text-zinc-500">{cfg.shipping_note}</p>}
                                                </div>
                                            )}
                                        </div>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="Email for your receipt (optional)"
                                            data-testid="cart-email-input"
                                            className="w-full rounded-full bg-white border border-zinc-300 px-5 py-3 text-sm outline-none focus:ring-2 focus:ring-pink placeholder:text-zinc-400"
                                        />
                                        <div className="space-y-1 font-manrope text-sm">
                                            <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span data-testid="cart-subtotal" className="font-bold">${subtotal.toFixed(2)}</span></div>
                                            {feeCents > 0 && <div className="flex justify-between"><span className="text-zinc-500">{FULFILLMENT_LABELS[method]} fee</span><span className="font-bold">{money(feeCents)}</span></div>}
                                            {feeNote && <p className="text-[11px] text-pink-dark font-semibold">{feeNote}</p>}
                                            <div className="flex justify-between border-t border-zinc-200 pt-2"><span className="text-zinc-500">Total before tax</span><span className="font-extrabold" data-testid="cart-total">{money(subtotalCents + feeCents)}</span></div>
                                            <p className="text-[11px] text-zinc-400">Method: {FULFILLMENT_LABELS[method]} · Tax calculated at checkout</p>
                                        </div>
                                        <button
                                            onClick={checkout}
                                            disabled={loading || !method}
                                            data-testid="cart-checkout-button"
                                            className="w-full rounded-full bg-pink text-ink py-4 font-oswald uppercase tracking-widest text-sm flex items-center justify-center gap-2 hover:bg-ink hover:text-white transition-colors duration-300 active:scale-95 disabled:opacity-50"
                                        >
                                            {loading ? "Starting checkout..." : <>Checkout — {FULFILLMENT_LABELS[method]} <ArrowRight size={15} /></>}
                                        </button>
                                        <p className="text-center text-[11px] text-zinc-400">Payments handled securely by Stripe.</p>
                                    </>
                                )}
                            </div>
                        )}
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
};
